/**
 * <stat-circles-group>
 * Drives the circular progress rings and count-up numbers in the
 * "stats-circles" section. The animation plays once, shortly after the
 * element connects to the page, and is skipped entirely when the visitor
 * has requested reduced motion (the ring/number simply render at their
 * final values instead).
 *
 * Note: this intentionally does NOT gate on scroll position /
 * IntersectionObserver. Those scroll-based reveals proved unreliable in
 * Shopify's theme-editor preview (sections are re-rendered into the page
 * via fetch + DOM replacement, and visibility/intersection timing in that
 * iframe context is inconsistent) — which left the counters stuck at 0%.
 * Animating on connect guarantees the count-up always plays, and is a
 * common, perfectly acceptable pattern for "stats" sections that live
 * near the top of a page anyway.
 */
(function () {
  if (customElements.get('stat-circles-group')) return;

  var CIRCLE_RADIUS = 52;
  var CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
  var ANIMATION_DURATION = 1400;

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  customElements.define(
    'stat-circles-group',
    class extends HTMLElement {
      connectedCallback() {
        // Guard against being connected more than once (e.g. the theme
        // editor re-inserting the section into the DOM).
        if (this._initialized) return;
        this._initialized = true;

        this.prefersReducedMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.circles = Array.prototype.slice.call(this.querySelectorAll('[data-stat-circle]'));

        var self = this;

        this.circles.forEach(function (circle) {
          var percentage = parseFloat(circle.getAttribute('data-percentage')) || 0;
          var progress = circle.querySelector('[data-stat-progress]');
          var offset = CIRCUMFERENCE * (1 - percentage / 100);

          if (progress) {
            progress.style.strokeDasharray = CIRCUMFERENCE.toFixed(2);
            circle.style.setProperty('--stat-circle-offset', offset.toFixed(2));
          }

          if (self.prefersReducedMotion) {
            self.reveal(circle, true);
          }
        });

        if (this.prefersReducedMotion) return;

        // requestAnimationFrame lets the browser finish layout/paint of the
        // freshly-connected markup before the transition + count-up start,
        // so the ring's initial (0%) state is reliably visible first.
        window.requestAnimationFrame(function () {
          self.circles.forEach(function (circle) {
            self.reveal(circle);
          });
        });
      }

      reveal(circle, skipAnimation) {
        if (circle.classList.contains('is-revealed')) return;
        circle.classList.add('is-revealed');

        var percentage = parseFloat(circle.getAttribute('data-percentage')) || 0;
        var numberEl = circle.querySelector('[data-stat-number]');
        if (!numberEl) return;

        if (skipAnimation) {
          numberEl.textContent = percentage;
          return;
        }

        var start = null;

        function step(timestamp) {
          if (start === null) start = timestamp;
          var elapsed = timestamp - start;
          var progress = Math.min(elapsed / ANIMATION_DURATION, 1);
          var eased = easeOutCubic(progress);
          numberEl.textContent = Math.round(eased * percentage);

          if (progress < 1) {
            window.requestAnimationFrame(step);
          } else {
            numberEl.textContent = percentage;
          }
        }

        window.requestAnimationFrame(step);
      }
    }
  );
})();
